import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Award,
  BookOpenCheck,
  CheckCircle2,
  Clock3,
  LogOut,
  ShieldCheck,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import SEO from "@/components/site/SEO";
import lmsApi from "@/lib/lmsApi";

function statusLabel(status) {
  return (
    {
      not_started: "Not started",
      in_progress: "In progress",
      completed: "Completed",
      failed: "Completion blocked",
      overdue: "Overdue",
    }[status] ||
    status?.replaceAll("_", " ") ||
    "Not started"
  );
}

function statusClass(status) {
  if (status === "completed") {
    return "bg-emerald-50 text-emerald-700";
  }

  if (status === "failed") {
    return "bg-red-50 text-red-700";
  }

  if (status === "overdue") {
    return "bg-amber-50 text-amber-700";
  }

  return "bg-slate-100 text-slate-700";
}

export default function LMSDashboard() {
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const me = await lmsApi.get("/auth/me");

        if (me.data.require_password_change) {
          navigate("/learn/change-password", {
            replace: true,
          });
          return;
        }

        if (me.data.is_platform_admin) {
          navigate("/learn/admin", {
            replace: true,
          });
          return;
        }

        const organisationMembership = me.data.memberships?.find((item) =>
          ["organisation_admin", "instructor", "auditor"].includes(item.role)
        );

        if (organisationMembership) {
          navigate(
            `/learn/organisations/${organisationMembership.organisation_id}`,
            {
              replace: true,
            }
          );
          return;
        }

        const response = await lmsApi.get("/learner/dashboard");

        if (active) {
          setData(response.data);
        }
      } catch (err) {
        if (err.response?.status === 401) {
          navigate("/learn/login", {
            replace: true,
          });
          return;
        }

        if (active) {
          setError(
            err.response?.data?.detail ||
              "We could not load your learning dashboard."
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [navigate]);

  const summary = useMemo(() => {
    const courses = data?.courses || [];

    return {
      total: courses.length,
      completed: courses.filter(
        (course) => course.status === "completed"
      ).length,
      inProgress: courses.filter(
        (course) => course.status === "in_progress"
      ).length,
    };
  }, [data]);

  const handleLogout = async () => {
    try {
      await lmsApi.post("/auth/logout");
    } finally {
      navigate("/learn/login", {
        replace: true,
      });
    }
  };

  if (loading) {
    return (
      <section className="min-h-[60vh] bg-slate-50 py-20">
        <div className="container-rail text-center text-slate-600">
          Loading TRAC-Learn…
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="min-h-[60vh] bg-slate-50 py-20">
        <div className="container-rail">
          <div className="mx-auto max-w-xl rounded-xl border border-red-200 bg-red-50 p-6 text-red-800">
            {error}
          </div>
        </div>
      </section>
    );
  }

  const learner = data?.learner;

  return (
    <>
      <SEO
        title="My Learning | TRAC-Learn"
        description="Your secure TRAC-Learn learning dashboard."
        path="/learn"
      />

      <section className="bg-navy py-12 text-white md:py-16">
        <div className="container-rail">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-center">
            <div>
              <p className="label-eyebrow text-safety">
                TRAC-Learn
              </p>

              <h1 className="mt-2 font-display text-4xl font-bold md:text-5xl">
                Welcome, {learner?.first_name}
              </h1>

              <p className="mt-3 max-w-2xl text-white/70">
                Continue your assigned learning and view your current progress.
              </p>
            </div>

            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/20 bg-white/5 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/10"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-10 md:py-14">
        <div className="container-rail">
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              [
                BookOpenCheck,
                "Assigned courses",
                summary.total,
              ],
              [
                Clock3,
                "In progress",
                summary.inProgress,
              ],
              [
                CheckCircle2,
                "Completed",
                summary.completed,
              ],
            ].map(([Icon, label, value]) => (
              <div
                key={label}
                className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
              >
                <div className="flex items-center gap-3 text-slate-500">
                  <Icon className="h-5 w-5 text-safety" />

                  <span className="text-sm font-semibold">
                    {label}
                  </span>
                </div>

                <p className="mt-3 font-display text-3xl font-bold text-navy">
                  {value}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-10 flex items-center justify-between gap-4">
            <div>
              <p className="label-eyebrow text-safety">
                My learning
              </p>

              <h2 className="mt-2 font-display text-3xl font-bold text-navy">
                Assigned courses
              </h2>
            </div>

            <div className="hidden items-center gap-2 text-sm text-slate-500 sm:flex">
              <ShieldCheck className="h-4 w-4 text-safety" />
              Secure learning record
            </div>
          </div>

          {data?.courses?.length ? (
            <div className="mt-6 grid gap-5 lg:grid-cols-2">
              {data.courses.map((course) => (
                <article
                  key={course.enrolment_id}
                  className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-safety">
                        Version {course.version_number}
                      </p>

                      <h3 className="mt-2 font-display text-2xl font-bold text-navy">
                        {course.title}
                      </h3>
                    </div>

                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${statusClass(
                        course.status
                      )}`}
                    >
                      {statusLabel(course.status)}
                    </span>
                  </div>

                  <div className="mt-6">
                    <div className="flex justify-between text-sm text-slate-600">
                      <span>Progress</span>

                      <span className="font-bold text-navy">
                        {course.progress_percent}%
                      </span>
                    </div>

                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-safety transition-all"
                        style={{
                          width: `${Math.max(
                            0,
                            Math.min(
                              100,
                              course.progress_percent
                            )
                          )}%`,
                        }}
                      />
                    </div>
                  </div>

                  <div className="mt-6 flex flex-wrap gap-4 text-xs text-slate-500">
                    {course.due_at && (
                      <span>
                        Due{" "}
                        {new Date(
                          course.due_at
                        ).toLocaleDateString("en-GB")}
                      </span>
                    )}

                    {course.completed_at && (
                      <span>
                        Completed{" "}
                        {new Date(
                          course.completed_at
                        ).toLocaleDateString("en-GB")}
                      </span>
                    )}
                  </div>

                  {course.status === "failed" && (
                    <div className="mt-5 flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />

                      A required assessment has exhausted its permitted
                      attempts. The record remains available for administrator
                      review.
                    </div>
                  )}

                  <div className="mt-6 flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          `/learn/course/${course.enrolment_id}`
                        )
                      }
                      className="inline-flex items-center justify-center rounded-lg bg-navy px-5 py-3 text-sm font-bold text-white transition hover:bg-navy/90"
                    >
                      {course.status === "not_started"
                        ? "Start course"
                        : course.status === "completed"
                        ? "Review course"
                        : course.status === "failed"
                        ? "View learning record"
                        : "Continue learning"}
                    </button>

                    {course.status === "completed" && (
                      <button
                        type="button"
                        onClick={() =>
                          navigate(
                            `/learn/certificate/${course.enrolment_id}`
                          )
                        }
                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-800"
                      >
                        <Award className="h-4 w-4" />
                        Certificate
                      </button>
                    )}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="mt-6 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
              <BookOpenCheck className="mx-auto h-9 w-9 text-slate-400" />

              <h3 className="mt-4 font-display text-xl font-bold text-navy">
                No courses assigned yet
              </h3>

              <p className="mt-2 text-slate-600">
                Your training administrator will assign learning to this
                account.
              </p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}